import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { AlertTriangle, Clock, Repeat, ChevronRight } from 'lucide-react';
import Layout from '../components/Layout';
import CompanyFilter from '../components/CompanyFilter';
import StatusBadge from '../components/StatusBadge';
import StatusDot from '../components/StatusDot';
import { api, formatCurrency, calculateInvoiceTotal } from '../lib/api';

const StatCard = ({ label, value, accent = false, testId, delay = '', loading = false }) => (
  <div data-testid={testId} className={`stat-card bg-surface border border-border p-6 fade-up ${delay} ${accent ? 'border-l-2 border-l-maroon' : ''}`}>
    <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-3">{label}</p>
    {loading ? (
      <div data-testid={`${testId}-skeleton`} className="h-9 w-32 bg-border/60 animate-pulse rounded-sm" />
    ) : (
      <p className="font-numbers text-3xl font-light text-text-primary tabular-nums">{formatCurrency(value)}</p>
    )}
  </div>
);

const Dashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [period, setPeriod] = useState('this_month');
  const [stats, setStats] = useState({ total_invoiced: 0, total_collected: 0, total_outstanding: 0, total_overdue: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [trend, setTrend] = useState([]);
  const [ageBuckets, setAgeBuckets] = useState([]);
  const [queue, setQueue] = useState({ overdue: [], due_this_week: [], recurring_due: [], total_count: 0, total_at_risk: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/companies').then(res => setCompanies(res.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (selectedCompany) params.company_id = selectedCompany;
    if (period && period !== 'all') params.period = period;
    queueMicrotask(() => setStatsLoading(true));
    
    api.get('/dashboard/stats', { params })
      .then(res => setStats(res.data))
      .finally(() => setStatsLoading(false));
    const trendParams = selectedCompany ? { company_id: selectedCompany } : {};
    api.get('/dashboard/monthly-trend', { params: trendParams }).then(res => setTrend(res.data));
    api.get('/reports/age-buckets', { params: trendParams }).then(res => setAgeBuckets(res.data));
    api.get('/invoices', { params: trendParams }).then(res => {
      // Filter by period client-side for recent list
      let filtered = res.data;
      const today = new Date();
      if (period === 'this_month') {
        const m = today.getMonth(), y = today.getFullYear();
        filtered = filtered.filter(inv => { const d = new Date(inv.date); return d.getMonth() === m && d.getFullYear() === y; });
      } else if (period === 'last_month') {
        const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const m = lm.getMonth(), y = lm.getFullYear();
        filtered = filtered.filter(inv => { const d = new Date(inv.date); return d.getMonth() === m && d.getFullYear() === y; });
      } else if (period === 'this_year') {
        const y = today.getFullYear();
        filtered = filtered.filter(inv => new Date(inv.date).getFullYear() === y);
      }
      setInvoices(filtered.slice(0, 10));
    });
    api.get('/payments').then(res => setPayments(res.data.slice(0, 5)));
    api.get('/dashboard/action-queue', { params: trendParams }).then(res => setQueue(res.data));
  }, [selectedCompany, period]);

  return (
    <Layout title="Dashboard" action={<CompanyFilter companies={companies} selected={selectedCompany} onChange={setSelectedCompany} />}>
      {/* Period Tabs */}
      <div data-testid="period-tabs" className="flex items-center gap-1 mb-6 border-b border-border">
        {[
          { id: 'this_month', label: 'This Month' },
          { id: 'last_month', label: 'Last Month' },
          { id: 'this_year', label: 'This Year' },
          { id: 'all', label: 'All Time' },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`period-${t.id}`}
            onClick={() => setPeriod(t.id)}
            className={`px-4 py-3 font-body text-sm transition-colors border-b-2 -mb-px ${
              period === t.id
                ? 'border-maroon text-maroon font-semibold'
                : 'border-transparent text-text-secondary hover:text-maroon'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div data-testid="dashboard-stats" className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard label="Total Invoiced" value={stats.total_invoiced} testId="stat-total-invoiced" loading={statsLoading} />
        <StatCard label="Collected" value={stats.total_collected} testId="stat-collected" delay="fade-up-d1" loading={statsLoading} />
        <StatCard label="Outstanding" value={stats.total_outstanding} testId="stat-outstanding" delay="fade-up-d2" loading={statsLoading} />
        <StatCard label="Overdue" value={stats.total_overdue} accent testId="stat-overdue" delay="fade-up-d3" loading={statsLoading} />
      </div>

      {/* Chart + Aging widget */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div data-testid="trend-chart" className="lg:col-span-2 bg-surface border border-border p-6 fade-up">
          <h2 className="font-heading text-xl font-light text-text-primary mb-6">Revenue Trend · Last 6 Months</h2>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid stroke="#D4CEC6" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="month" stroke="#4A4A4A" tick={{ fontFamily: 'Outfit', fontSize: 12, fontWeight: 300 }} axisLine={{ stroke: '#D4CEC6' }} tickLine={false} />
                <YAxis stroke="#4A4A4A" tick={{ fontFamily: 'EB Garamond', fontSize: 12 }} axisLine={{ stroke: '#D4CEC6' }} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: '#EDE8E0', border: '1px solid #D4CEC6', borderRadius: 0, fontFamily: 'Outfit', fontWeight: 300 }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Legend wrapperStyle={{ fontFamily: 'Outfit', fontWeight: 300, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.2em' }} />
                <Bar dataKey="invoiced" fill="#5C1A2A" name="Invoiced" />
                <Bar dataKey="collected" fill="#2A4A35" name="Collected" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="aging-widget" className="bg-surface border border-border p-6 fade-up fade-up-d1">
          <div className="flex justify-between items-baseline mb-2">
            <h2 className="font-heading text-xl font-light text-text-primary">Aging</h2>
            <button data-testid="view-reports-link" onClick={() => navigate('/reports')} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">Reports →</button>
          </div>
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-6">Outstanding by age</p>
          
          <div className="space-y-4">
            {ageBuckets.map((b, i) => {
              const colors = ['#2A4A35', '#B8A96A', '#7A2438', '#5C1A2A'];
              const max = Math.max(...ageBuckets.map(x => x.amount), 1);
              const pct = (b.amount / max) * 100;
              return (
                <div key={b.bucket}>
                  <div className="flex justify-between mb-1">
                    <span className="font-body text-xs uppercase tracking-widest text-text-secondary mono-num">{b.bucket} d</span>
                    <span className="font-numbers text-sm tabular-nums" style={{ color: b.amount > 0 ? colors[i] : '#4A4A4A' }}>{formatCurrency(b.amount)}</span>
                  </div>
                  <div className="h-1 bg-border w-full overflow-hidden">
                    <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: colors[i] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* This Week Action Queue */}
      <div data-testid="action-queue-widget" className="bg-surface border border-border p-6 mb-8 fade-up">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— Action Queue</p>
            <h2 className="font-heading text-xl font-light text-text-primary">This Week</h2>
          </div>
          <div className="text-right">
            <p data-testid="action-queue-count" className="font-numbers text-3xl font-light text-text-primary tabular-nums">{queue.total_count}</p>
            <p className="font-body text-xs text-text-secondary uppercase tracking-wider">items · {formatCurrency(queue.total_at_risk)} at risk</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Overdue */}
          <div data-testid="queue-overdue">
            <div className="flex items-center gap-2 pb-2 mb-3 border-b border-border">
              <AlertTriangle className="w-3.5 h-3.5 text-maroon" strokeWidth={1.5} />
              <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon font-semibold">Overdue</p>
              <span className="font-body text-xs text-text-secondary ml-auto">{queue.overdue.length}</span>
            </div>
            <div className="space-y-2 max-h-72 overflow-auto">
              {queue.overdue.length === 0 && <p className="font-body text-xs text-text-secondary py-2">Nothing overdue — nice.</p>}
              {queue.overdue.map(item => (
                <button key={item.id} data-testid={`queue-overdue-${item.invoice_no}`} onClick={() => navigate(`/invoices/${item.id}`)} className="w-full text-left p-3 border border-border hover:border-maroon hover:bg-cream transition-colors group">
                  <div className="flex items-baseline justify-between">
                    <p className="font-body text-sm font-medium text-text-primary truncate">{item.client_name || '—'}</p>
                    <ChevronRight className="w-3 h-3 text-text-secondary group-hover:text-maroon flex-shrink-0" strokeWidth={1.5} />
                  </div>
                  <div className="flex items-baseline justify-between mt-1">
                    <p className="font-body text-[11px] text-text-secondary">{item.invoice_no} · <span className="text-maroon">{item.days_overdue}d late</span></p>
                    <p className="font-numbers text-xs text-maroon tabular-nums">{formatCurrency(item.balance_due)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Due This Week */}
          <div data-testid="queue-due-this-week">
            <div className="flex items-center gap-2 pb-2 mb-3 border-b border-border">
              <Clock className="w-3.5 h-3.5 text-text-primary" strokeWidth={1.5} />
              <p className="font-body text-xs uppercase tracking-[0.2em] text-text-primary font-semibold">Due Next 7 Days</p>
              <span className="font-body text-xs text-text-secondary ml-auto">{queue.due_this_week.length}</span>
            </div>
            <div className="space-y-2 max-h-72 overflow-auto">
              {queue.due_this_week.length === 0 && <p className="font-body text-xs text-text-secondary py-2">All clear this week.</p>}
              {queue.due_this_week.map(item => (
                <button key={item.id} data-testid={`queue-due-${item.invoice_no}`} onClick={() => navigate(`/invoices/${item.id}`)} className="w-full text-left p-3 border border-border hover:border-maroon hover:bg-cream transition-colors group">
                  <div className="flex items-baseline justify-between">
                    <p className="font-body text-sm font-medium text-text-primary truncate">{item.client_name || '—'}</p>
                    <ChevronRight className="w-3 h-3 text-text-secondary group-hover:text-maroon flex-shrink-0" strokeWidth={1.5} />
                  </div>
                  <div className="flex items-baseline justify-between mt-1">
                    <p className="font-body text-[11px] text-text-secondary">{item.invoice_no} · in {item.days_until_due}d</p>
                    <p className="font-numbers text-xs text-text-primary tabular-nums">{formatCurrency(item.balance_due)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Recurring Due */}
          <div data-testid="queue-recurring-due">
            <div className="flex items-center gap-2 pb-2 mb-3 border-b border-border">
              <Repeat className="w-3.5 h-3.5 text-text-primary" strokeWidth={1.5} />
              <p className="font-body text-xs uppercase tracking-[0.2em] text-text-primary font-semibold">Recurring Generating</p>
              <span className="font-body text-xs text-text-secondary ml-auto">{queue.recurring_due.length}</span>
            </div>
            <div className="space-y-2 max-h-72 overflow-auto">
              {queue.recurring_due.length === 0 && <p className="font-body text-xs text-text-secondary py-2">No retainers due this week.</p>}
              {queue.recurring_due.map(item => (
                <button key={item.id} data-testid={`queue-recurring-${item.id}`} onClick={() => navigate('/recurring')} className="w-full text-left p-3 border border-border hover:border-maroon hover:bg-cream transition-colors group">
                  <div className="flex items-baseline justify-between">
                    <p className="font-body text-sm font-medium text-text-primary truncate">{item.client_name || item.template_name}</p>
                    <ChevronRight className="w-3 h-3 text-text-secondary group-hover:text-maroon flex-shrink-0" strokeWidth={1.5} />
                  </div>
                  <div className="flex items-baseline justify-between mt-1">
                    <p className="font-body text-[11px] text-text-secondary">{item.template_name} · in {item.days_until_due}d</p>
                    <p className="font-numbers text-xs text-text-primary tabular-nums">{formatCurrency(item.amount)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-testid="recent-invoices-table" className="lg:col-span-2 bg-surface border border-border p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading text-xl font-light text-text-primary">Recent Invoices</h2>
            <button data-testid="view-all-invoices" onClick={() => navigate('/invoices')} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">View All</button>
          </div>
          <table className="w-full">
            <thead>
              <tr>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Invoice</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Client</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const { total } = calculateInvoiceTotal(inv);
                return (
                  <tr key={inv.id} data-testid={`dashboard-invoice-row-${inv.invoice_no}`} className="border-b border-border hover:bg-cream cursor-pointer transition-colors" onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <td className="py-4 font-body text-sm text-text-primary">{inv.invoice_no}</td>
                    <td className="py-4 font-body text-sm text-text-primary">{inv.client_name}</td>
                    <td className="py-4 font-numbers text-sm text-text-primary text-right tabular-nums">{formatCurrency(total)}</td>
                    <td className="py-4 text-center"><span className="inline-flex items-center"><StatusDot status={inv.status} pulse={inv.status === 'overdue'} /><StatusBadge status={inv.status} /></span></td>
                  </tr>
                );
              })}
              {invoices.length === 0 && (
                <tr><td colSpan="4" className="py-8 text-center text-text-secondary font-body text-sm">No invoices yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div data-testid="recent-payments-table" className="bg-surface border border-border p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading text-xl font-light text-text-primary">Recent Payments</h2>
            <button data-testid="view-all-payments" onClick={() => navigate('/payments')} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">View All</button>
          </div>
          <div className="space-y-4">
            {payments.map(p => (
              <div key={p.id} className="border-b border-border pb-4 last:border-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-body text-sm text-text-primary">{p.client_name}</p>
                    <p className="font-body text-xs text-text-secondary mt-1">{p.invoice_no} · {p.mode}</p>
                  </div>
                  <p className="font-numbers text-base text-success tabular-nums">{formatCurrency(p.amount)}</p>
                </div>
              </div>
            ))}
            {payments.length === 0 && (
              <p className="text-center text-text-secondary font-body text-sm py-8">No payments yet</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
